import { Translations, UpdateOnboardingAnalytics } from "src/utils/types";
import styles from "./style.module.scss";
import { UIButton, UIText } from "src/components/uIKit";
import Separator from "src/components/separator";
import classNames from "classnames";
import { useAppDispatch, useAppSelector } from "src/hooks/useRedux";
import { images } from "src/assets/images/images";
import { resetPopupInfo } from "src/redux/slices/popupSlice/popupSlice";
import {
  __DEV__,
  getUrlParams,
  sentPostMessage,
  warningPopupHeight,
} from "src/utils/utils";
import DigitalAgentContent from "./digitalAgentContent";
import FAQContent from "./fAQContent";
import {
  API_RESULT,
  DIGITAL_AGENT_BUTTONS,
  POPUP_TYPE,
  ROUTES,
  URL_SOURCE_TYPE,
} from "src/utils/enums";
import { useIsDigitalOrDigitalAgent } from "src/hooks/useIsDigitalOrDigitalAgent";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useNavigate } from "react-router-dom";

interface PopupTemplateProps {
  title: string;
  subTitle?: string;
  body: string;
  img: string;
  withButton?: boolean;
  mobileImg?: string;
  okBtnText?: string;
  popupType: string;
  cancelBtnText?: string;
  strings?: Translations;
  hideCloseBtn?: boolean;
  errorStatus?: number;
  buyPolicyBtnText?: string;
  withBuyPolicyBtn?: boolean;
}

const PopupTemplate = ({
  title,
  subTitle = "",
  body,
  img,
  withButton = false,
  mobileImg,
  okBtnText,
  cancelBtnText,
  popupType,
  strings,
  hideCloseBtn = true,
  errorStatus,
  buyPolicyBtnText,
  withBuyPolicyBtn = false,
}: PopupTemplateProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isDigitalOrAgent = useIsDigitalOrDigitalAgent();
  const isMobile = useIsMobile();
  const {
    fXEligibilityData,
    encryptedPersonalId,
    businessUnit,
    travelPolicyNumber,
  } = useAppSelector((state) => state.app);

  const closePopup = () => {
    if (isDigitalOrAgent && popupType !== POPUP_TYPE.FAQ) { // on faq we don't want to send post message
      const data = { buttonClicked: DIGITAL_AGENT_BUTTONS.BACK_TO_BENEFITS };
      sentPostMessage(data);
    }
    dispatch(resetPopupInfo());
    // dispatch(resetPopupInfo());
  };
  const getEnsureParams = () => {
    let params = "";
    if (encryptedPersonalId) params += `&personalId=${encryptedPersonalId}`;
    if (businessUnit) params += `&businessUnit=${businessUnit}`;
    if (travelPolicyNumber)
      params += `&travelPolicyNumber=${travelPolicyNumber}`;
    return params;
  };

  const openLinkInNewTab = (link: string) => {
    window.open(link, "_blank");
  };

  const onSubmit = () => {
    if (errorStatus == 401 || errorStatus == 403) {
      const currentSource = window.location.pathname
        .split("/")[1]
        .toLocaleLowerCase();

      switch (currentSource) {
        case URL_SOURCE_TYPE.ENSURE:
        case URL_SOURCE_TYPE.PFS_REP:
          // call checkFXEligibility API  #1248  and only in case of success show 3 option screen
          let url = "";
          if (currentSource === URL_SOURCE_TYPE.ENSURE) {
            url = `/${currentSource}/agent?${getEnsureParams()}`;
          } else {
            url = `/${currentSource}/${ROUTES.PC_PLUS_SCREEN}`;
          }
          navigate(url);
          break;
        case URL_SOURCE_TYPE.SMS:
        case URL_SOURCE_TYPE.SMS_SHARE:
        case URL_SOURCE_TYPE.MARKETING:
        case URL_SOURCE_TYPE.CAMPAIGN:
        case URL_SOURCE_TYPE.REP_MANUAL:
        case URL_SOURCE_TYPE.APP:
          //navigate to pcplus screen
          // navigate(`/${currentSource}/${ROUTES.LOGIN}/${ROUTES.LOGIN_DETAILS}`);
          navigate(`/${currentSource}/${ROUTES.PC_PLUS_SCREEN}?${getUrlParams()}`);
          break;

        case URL_SOURCE_TYPE.DIGITAL_AGENT:
        case URL_SOURCE_TYPE.DIGITAL:
          //do not show pop up and send post message with RC=15
          const data = {
            status: API_RESULT.FAILED,
            responseCode: "15",
          };
          sentPostMessage(data);
          break;
        default:
          break;
      }
    } else {
      const data = {
        status: API_RESULT.FAILED,
        collectCardInstruction: fXEligibilityData.collectCardInstruction,
        passportCard4LastDigits: fXEligibilityData.passportCard4LastDigits,
      };
      sentPostMessage(data);
    }

    dispatch(resetPopupInfo());
  };

  const renderContent = () => {
    switch (popupType) {
      case POPUP_TYPE.WARNING:
        return (
          <div className={styles.errorSubTitleWrapper}>
            <div className={styles.contentSection}>
              <div className={styles.mBottom}>
                {subTitle && <UIText text={subTitle} size={22} weight={500} />}
              </div>

              <UIText
                text={body}
                size={18}
                weight={400}
                style={classNames(styles.cardSubTitle, styles.flex1)}
              />
            </div>

            <div className={classNames(styles.buttonsSpecificWrapper, isMobile ? styles.flexColumn : styles.flexRow)}>
              {withBuyPolicyBtn && buyPolicyBtnText && (
                <div className={styles.buttonWrapepr}>
                  <UIButton title={buyPolicyBtnText || ""} onSubmit={() => {
                    openLinkInNewTab("https://buy.passportcard.co.il/existing")
                  }} />
                </div>
              )}
              {withButton && okBtnText && (
                <div className={styles.buttonWrapepr}>
                  <UIButton title={okBtnText} onSubmit={onSubmit} />
                </div>
              )}
             
            </div>
          </div>
        );

      case POPUP_TYPE.DIGITAL_AGENT:
        return <DigitalAgentContent body={body} strings={strings} />;
      case POPUP_TYPE.FAQ:
        return (
          <FAQContent
            body={body}
            strings={strings}
            subTitle={subTitle}
            okBtnText={okBtnText}
          />
        );

      default:
        return;
    }
  };
  return (
    <div className={styles.modalView}>
      <div
        className={styles.container}
        style={{ height: isMobile ? warningPopupHeight : "auto" }}
      >
        <div className={styles.card}>
          {!isMobile && (
            <div>
              <img alt="agent" src={img} className={styles.agentImage} />
            </div>
          )}

          <div className={styles.infoPart}>
            <div className={styles.popupHeader}>
              <UIText
                tabIndex={0}
                // announce={true}
                text={title}
                size={title.length >= 35 ? 24 : 26}
                weight={500}
                style={styles.cardTitle}
              />

              {!hideCloseBtn && (
                <div
                  tabIndex={0}
                  className={styles.closeWrapper}
                  onClick={closePopup}
                  onKeyUp={(e) =>
                    e.key === "Enter" || e.key === " " ? closePopup() : null
                  }
                >
                  <img
                    alt="close"
                    src={images.close}
                    className={styles.close}
                  />
                </div>
              )}
            </div>
            <Separator
              className={styles.separator}
              separatorWrapper={styles.separatorWrapper}
            />
            <div
              className={
                isMobile
                  ? styles.contantContainerMobile
                  : styles.contantContainer
              }
            >
              {isMobile && (
                <div>
                  <img
                    alt="agent"
                    src={mobileImg}
                    className={styles.mobileImg}
                  />
                </div>
              )}

              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupTemplate;
